import { colors } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { Order, OrderStatus } from '~/services/api.server';
import type { ShipmentStatus as TShipmentStatus } from '~/services/shipments.server';
import { OrderStatusId, ShipmentStatusId } from '~/utils/constants';
import theme from '~/utils/mui/theme';

export function Status({ text, color, secondaryColor }: { text: string; color: string; secondaryColor: string }) {
  return (
    <Typography
      component="span"
      display="inline-block"
      px={1}
      borderRadius={0.5}
      fontSize={12}
      fontWeight={500}
      lineHeight="20px"
      border={`1px solid ${color}`}
      color={color}
      bgcolor={secondaryColor}
      textAlign="center"
    >
      {text}
    </Typography>
  );
}

export const statusColors = {
  green: { main: colors.green[500], secondary: colors.green[100] },
  blue: { main: theme.palette.primary.dark, secondary: colors.blue[100] },
  red: { main: colors.red[400], secondary: colors.red[50] },
  grey: { main: colors.grey[600], secondary: colors.grey[300] },
  orange: { main: theme.palette.warning.main, secondary: colors.orange[50] },
};

export function PaymentStatus({ id, name }: { id: keyof typeof paymentStatusColors; name: string }) {
  return <Status text={name} color={paymentStatusColors[id].main} secondaryColor={paymentStatusColors[id].secondary} />;
}

export const paymentStatusColors = {
  pending_payment: { main: statusColors.grey.main, secondary: statusColors.grey.secondary },
  processing_payment: { main: statusColors.blue.main, secondary: statusColors.blue.secondary },
  paid: { main: statusColors.green.main, secondary: statusColors.green.secondary },
  payment_failed: { main: statusColors.red.main, secondary: statusColors.red.secondary },
  payment_canceled: { main: statusColors.red.main, secondary: statusColors.red.secondary },
  payment_refunded: { main: statusColors.green.main, secondary: statusColors.green.secondary },
} as const;

export function ShipmentStatus({ status }: { status: TShipmentStatus }) {
  return (
    <Status
      text={status.name}
      color={shipStatusColors[status.id].main}
      secondaryColor={shipStatusColors[status.id].secondary}
    />
  );
}

const shipStatusColors = {
  [ShipmentStatusId.CREATED]: { main: statusColors.green.main, secondary: statusColors.green.secondary },
  [ShipmentStatusId.IN_TRANSIT]: { main: statusColors.orange.main, secondary: statusColors.orange.secondary },
  [ShipmentStatusId.DELIVERED]: { main: statusColors.blue.main, secondary: statusColors.blue.secondary },
  [ShipmentStatusId.CANCELED]: { main: statusColors.red.main, secondary: statusColors.red.secondary },
};

export function ProductionStatusChip({ status }: { status: OrderStatus }) {
  return (
    <Status
      text={status.name}
      color={productionStatusColors[status.id].main}
      secondaryColor={productionStatusColors[status.id].secondary}
    />
  );
}

const productionStatusColors: Record<Order['status']['id'], { main: string; secondary: string }> = {
  [OrderStatusId.PENDING]: { main: statusColors.orange.main, secondary: statusColors.orange.secondary },
  [OrderStatusId.IN_PRODUCTION]: { main: statusColors.blue.main, secondary: statusColors.blue.secondary },
  [OrderStatusId.COMPLETE]: { main: statusColors.red.main, secondary: statusColors.red.secondary },
  [OrderStatusId.CANCELED]: { main: statusColors.red.main, secondary: statusColors.red.secondary },
};
